import React, { useContext, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { getScenario } from 'gqlQueries/priceSimulator';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { simulationInputDataParser, simulationOutputDataParser } from 'parser/priceSimulator/priceSimulatorDataParser';
import { UserContext } from 'contexts/ConfigContext';
import { formatNumWithKMB, getConfig, getPercentageChangeV2 } from 'utils/helperMethods';
const errorMessage: string = 'Something went wrong!';

function useFetchScenarioForComparison() {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<Array<string> | null>(null);
    const navigate = useNavigate();
    const { appConfig } = useContext(UserContext);
    const { inputKeyMapping, levels, outputKeyMapping, recommendationKeyMapping } = getConfig(
        'priceSimulator',
        appConfig,
    );
    const [fetchScenario] = useLazyQuery(getScenario, {
        onCompleted: onFetchComplete,
        onError: onFetchError,
        fetchPolicy: 'no-cache',
    });

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { getScenarioCompleteData } = payload;

            const scenarioOutput: any = {};

            //parse goal inputs and comparison data
            if (
                getScenarioCompleteData &&
                'goalInputs' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.goalInputs)
            ) {
                const parsedKpiMetricData = getScenarioCompleteData.goalInputs?.map((item: any) => JSON.parse(item));
                scenarioOutput.parsedKpiMetricData = parsedKpiMetricData.map((item: any) => {
                    return {
                        KPI: item.name,
                        Actuals: item.actualValue,
                        Goal: item.goalValue,
                        Simulated: item.simulatedValue,
                        'Simulated vs Actuals': Number(item.simulatedValue) - Number(item.actualValue),
                        'Simulated vs Goal': Number(item.simulatedValue) - Number(item.goalValue),
                    };
                });

                // adding comparison data
                scenarioOutput.scenarioComparisonData = {
                    OutputVsActuals: parsedKpiMetricData.map((item: any) => {
                        const absChange = Number(item.simulatedValue) - Number(item.actualValue);
                        return {
                            KPI: item.name,
                            absChg: absChange,
                            percentageChg: getPercentageChangeV2(item.actualValue, item.simulatedValue),
                            value: item.actualValue,
                        };
                    }),
                    OutputVsGoals: parsedKpiMetricData.map((item: any) => {
                        const absChange = Number(item.simulatedValue) - Number(item.goalValue || item.actualValue);
                        return {
                            KPI: item.name,
                            absChg: absChange,
                            percentageChg: getPercentageChangeV2(item.goalValue, item.simulatedValue),
                            value: item.goalValue || item.actualValue,
                        };
                    }),
                };
            }
            //parse global filter
            if (
                getScenarioCompleteData &&
                'globalFilter' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.globalFilter)
            ) {
                scenarioOutput.GlobalFilters = [
                    ...getScenarioCompleteData.globalFilter?.map((item: any) => JSON.parse(item)),
                ];
            }
            //parse start and end date
            if (
                getScenarioCompleteData &&
                'startDate' in getScenarioCompleteData &&
                'endDate' in getScenarioCompleteData
            ) {
                const startDate = dayjs(getScenarioCompleteData.startDate).add(6, 'day').toDate();
                const endDate = dayjs(getScenarioCompleteData.endDate).add(6, 'day').toDate();
                scenarioOutput.SimulationDateRange = [startDate, endDate];
            }

            //parse simulator output
            if (
                getScenarioCompleteData &&
                'simulatorOutput' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.simulatorOutput)
            ) {
                const parsedData: any = simulationOutputDataParser(
                    getScenarioCompleteData?.simulatorOutput,
                    outputKeyMapping,
                );
                scenarioOutput.outputParsedData = parsedData;
            }

            //parse simulator inputs
            if (
                getScenarioCompleteData &&
                'simulatorInputs' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.simulatorInputs)
            ) {
                const parsedData: any = simulationInputDataParser(
                    getScenarioCompleteData.simulatorInputs,
                    levels,
                    inputKeyMapping,
                );
                scenarioOutput.inputParsedData = parsedData;
            }

            //parse simulator recommendationData
            if (
                getScenarioCompleteData &&
                'recommendationOutput' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.recommendationOutput)
            ) {
                const parsedData: any = simulationOutputDataParser(
                    getScenarioCompleteData.recommendationOutput,
                    recommendationKeyMapping,
                );

                scenarioOutput.recommendationOutput = parsedData;
            }

            //parse scenario name
            if (getScenarioCompleteData && 'scenarioName' in getScenarioCompleteData) {
                scenarioOutput.scenarioName = getScenarioCompleteData?.scenarioName;
            }

            setData(scenarioOutput);
        } catch (error) {
            console.error(errorMessage, error);
            setError('failed');
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData([]);
        setLoading(false);
        setError(error);
    }

    return {
        loading,
        error,
        data,
        fetchScenario: (variables: any) => {
            setLoading(true);
            setData(null);
            setError(null);
            fetchScenario(variables);
        },
    };
}

export { useFetchScenarioForComparison };
